import {
	Dialog,
	DialogContent,
	DialogTitle,
	LinearProgress,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { generateErrorState, handleValidateObj, isEditorEmpty, sanitizedConfig } from "helpers/utils";
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { showError } from "../../../redux/common/actions";
import AddDialogStyle from "styles/application/AddDialogStyle";
import * as yup from "yup";
import ColourConstants from "helpers/colourConstants";
import TextEditor from 'components/Elements/TextEditor';
import sanitizeHtml from "sanitize-html";


const schema = yup.object({
	note: yup
		.string("This field must be a string")
		.required("This field is required"),
});

const ADD = AddDialogStyle();
const defaultData = { note: "" };
const defaultError = { note: null };

const useStyles = makeStyles()((theme) => ({
	dialogContent: {
		display: "flex",
		flexDirection: "column",
	},
	createButton: {
		width: "auto",
	},
}));

const AddNoteDialog = ({ open, handleClose, createHandler, updateHandler, editData }) => {
	const dispatch = useDispatch();
	const { classes } = useStyles();
	const [input, setInput] = useState(defaultData);
	const [errors, setErrors] = useState(defaultError);
	const [isUpdating, setIsUpdating] = useState(false);
	const isEditMode = Boolean(editData);

	useEffect(() => {
		if (editData) {
			setInput({ note: editData.note });
		} else {
			setInput(defaultData);
		}
	}, [editData]);

	const closeOverride = () => {
		setInput(defaultData);
		setErrors(defaultError);

		handleClose();
	};

	const handleCreateProcess = async () => {
		setIsUpdating(true);

		try {
			// Sanitize the HTML before validation and saving
			const sanitizedNote = sanitizeHtml(input.note, sanitizedConfig);

			const localChecker = await handleValidateObj(schema, { note: sanitizedNote });

			if (!localChecker.some((el) => el.valid === false)) {
				const newData = isEditMode
					? await updateHandler(editData.id, sanitizedNote)
					: await createHandler(sanitizedNote);
				if (newData?.success) {
					setIsUpdating(false);
					closeOverride();
				} else {
					if (newData?.errors) {
						setErrors({ ...errors, ...newData.errors });
					}
					setIsUpdating(false);
				}
			} else {
				const newErrors = generateErrorState(localChecker);
				setErrors({ ...errors, ...newErrors });
				setIsUpdating(false);
			}
		} catch (err) {
			dispatch(showError(`Failed to ${isEditMode ? 'update' : 'add'} note.`));
			setIsUpdating(false);
			closeOverride();
		}
	};

	const handleDialogEntered = () => {
		setTimeout(() => {
			const quillEditorDiv = document.querySelector('.ql-editor');
			if (quillEditorDiv) {
				quillEditorDiv.focus();
			}
		}, 50);
	};

	return (
		<Dialog
			open={open}
			onClose={closeOverride}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
			TransitionProps={{
				onEntered: handleDialogEntered,
			}}
		>
			{isUpdating ? <LinearProgress /> : null}
			<ADD.ActionContainer>
				<DialogTitle id="alert-dialog-title">
					{<ADD.HeaderText>{isEditMode ? "Edit Note" : "Add Note"}</ADD.HeaderText>}
				</DialogTitle>
				<ADD.ButtonContainer>
					<ADD.CancelButton
						onClick={closeOverride}
						variant="contained"
						sx={{
							"&.MuiButton-root:hover": {
								backgroundColor: ColourConstants.deleteDialogHover,
								color: "#ffffff",
							},
						}}
					>
						Cancel
					</ADD.CancelButton>
					<ADD.ConfirmButton
						onClick={handleCreateProcess}
						variant="contained"
						className={classes.createButton}
						disabled={isUpdating || isEditorEmpty(input)}
						sx={{
							"&.MuiButton-root:hover": {
								backgroundColor: ColourConstants.deleteDialogHover,
								color: "#ffffff",
							},
						}}
					>
						{isEditMode ? "Save" : "Add Note"}
					</ADD.ConfirmButton>
				</ADD.ButtonContainer>
			</ADD.ActionContainer>
			<DialogContent className={classes.dialogContent}>
				<div>
					<TextEditor
						placeholder="Note"
						value={input.note || ""}
						onChange={(content, delta, source, editor) => {
							setInput({ note: content, editor });
						}}
						withColors={true}
					/>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default AddNoteDialog;
